import type { CoverageValues } from '@/types/app';

/**
 * Standard waiting period options supported by most insurers
 */
export const STANDARD_WAITING_PERIODS = [10, 20, 30, 65, 130, 260];

/**
 * Standard employer cost options supported by most insurers
 */
export const STANDARD_EMPLOYER_COSTS = [0, 10, 15, 20, 25, 30];

/**
 * Standard coverage percentage options supported by most insurers
 */
export const STANDARD_COVERAGE_PERCENTAGES = [50, 60, 70, 80, 90, 100];

/**
 * Exotic value thresholds that typically result in fewer quotes
 */
export const EXOTIC_THRESHOLDS = {
	// Waiting periods outside the standard range
	MIN_STANDARD_WAITING: 10,
	MAX_STANDARD_WAITING: 260,
	// Zero coverage in first year (effectively same as longer waiting period)
	MIN_FIRST_YEAR_COVERAGE: 50,
	// High employer costs
	MAX_STANDARD_EMPLOYER_COSTS: 30
};

/**
 * Issue types for exotic values
 */
export type CoverageIssueType =
	| 'exotic_waiting_period'
	| 'zero_first_year_coverage'
	| 'unusual_coverage_progression'
	| 'high_employer_costs'
	| 'non_standard_employer_costs'
	| 'no_issues';

/**
 * Represents a detected issue with coverage values
 */
export interface CoverageIssue {
	type: CoverageIssueType;
	field: keyof CoverageValues | 'multiple';
	currentValue: number | string;
	suggestedValue: number;
	message: string;
	severity: 'warning' | 'info';
}

/**
 * Detects if the waiting period is exotic (unusual/unsupported by most insurers)
 */
function isExoticWaitingPeriod(waitingPeriod: number): boolean {
	return !STANDARD_WAITING_PERIODS.includes(waitingPeriod);
}

/**
 * Detects if first year coverage is zero or very low
 */
function hasZeroOrLowFirstYearCoverage(firstHalf: number, secondHalf: number): boolean {
	return (
		firstHalf < EXOTIC_THRESHOLDS.MIN_FIRST_YEAR_COVERAGE ||
		secondHalf < EXOTIC_THRESHOLDS.MIN_FIRST_YEAR_COVERAGE
	);
}

/**
 * Detects unusual coverage progression (e.g., second year higher than first year)
 */
function hasUnusualCoverageProgression(values: CoverageValues): boolean {
	const firstYearAvg =
		(values.coverage_percentage_first_year_first_half +
			values.coverage_percentage_first_year_second_half) /
		2;
	const secondYearAvg =
		(values.coverage_percentage_second_year_first_half +
			values.coverage_percentage_second_year_second_half) /
		2;

	// Second year coverage should typically be equal or lower than first year
	return secondYearAvg > firstYearAvg;
}

/**
 * Finds the nearest standard waiting period
 */
function findNearestWaitingPeriod(waitingPeriod: number): number {
	// Special case: very high waiting periods should map to 260
	if (waitingPeriod > 260) {
		return 260;
	}

	// Special case: very low waiting periods should map to 10
	if (waitingPeriod < 10) {
		return 10;
	}

	// Find the closest standard period
	return STANDARD_WAITING_PERIODS.reduce((prev, curr) => {
		return Math.abs(curr - waitingPeriod) < Math.abs(prev - waitingPeriod) ? curr : prev;
	});
}

/**
 * Suggests normalized values for exotic coverage configuration
 */
export function suggestNormalizedCoverage(values: CoverageValues): CoverageValues {
	const normalized: CoverageValues = { ...values };

	// Normalize waiting period if exotic
	if (isExoticWaitingPeriod(values.waiting_period)) {
		normalized.waiting_period = findNearestWaitingPeriod(values.waiting_period);
	}

	// Normalize first year coverage if zero or very low
	if (
		values.coverage_percentage_first_year_first_half < EXOTIC_THRESHOLDS.MIN_FIRST_YEAR_COVERAGE
	) {
		normalized.coverage_percentage_first_year_first_half = 70;
	}
	if (
		values.coverage_percentage_first_year_second_half < EXOTIC_THRESHOLDS.MIN_FIRST_YEAR_COVERAGE
	) {
		normalized.coverage_percentage_first_year_second_half = 70;
	}

	// Normalize employer costs to nearest standard value (for consistency, not because it's problematic)
	if (!STANDARD_EMPLOYER_COSTS.includes(values.employer_costs)) {
		// Find nearest standard value
		const nearest = STANDARD_EMPLOYER_COSTS.reduce((prev, curr) => {
			return Math.abs(curr - values.employer_costs) < Math.abs(prev - values.employer_costs)
				? curr
				: prev;
		});
		normalized.employer_costs = nearest;
	}

	return normalized;
}

/**
 * Detects all exotic values in coverage configuration and returns issues
 * The message field will be empty - use translateCoverageIssue() to get localized messages
 */
export function detectCoverageIssues(values: CoverageValues): CoverageIssue[] {
	const issues: CoverageIssue[] = [];

	// Check waiting period
	if (isExoticWaitingPeriod(values.waiting_period)) {
		const suggested = findNearestWaitingPeriod(values.waiting_period);
		issues.push({
			type: 'exotic_waiting_period',
			field: 'waiting_period',
			currentValue: values.waiting_period,
			suggestedValue: suggested,
			message: '', // Will be filled by translateCoverageIssue()
			severity: 'warning'
		});
	}

	// Check first year coverage
	if (
		hasZeroOrLowFirstYearCoverage(
			values.coverage_percentage_first_year_first_half,
			values.coverage_percentage_first_year_second_half
		)
	) {
		issues.push({
			type: 'zero_first_year_coverage',
			field: 'multiple',
			currentValue: `${values.coverage_percentage_first_year_first_half}% / ${values.coverage_percentage_first_year_second_half}%`,
			suggestedValue: 70,
			message: '', // Will be filled by translateCoverageIssue()
			severity: 'warning'
		});
	}

	// Check coverage progression
	if (hasUnusualCoverageProgression(values)) {
		issues.push({
			type: 'unusual_coverage_progression',
			field: 'multiple',
			currentValue: 'tweede jaar > eerste jaar',
			suggestedValue: 70,
			message: '', // Will be filled by translateCoverageIssue()
			severity: 'info'
		});
	}

	// Check employer costs - distinguish between high (>30%) and non-standard (not in list)
	if (!STANDARD_EMPLOYER_COSTS.includes(values.employer_costs)) {
		const suggested = STANDARD_EMPLOYER_COSTS.reduce((prev, curr) => {
			return Math.abs(curr - values.employer_costs) < Math.abs(prev - values.employer_costs)
				? curr
				: prev;
		});

		// If > 30%, it's actually too high
		if (values.employer_costs > EXOTIC_THRESHOLDS.MAX_STANDARD_EMPLOYER_COSTS) {
			issues.push({
				type: 'high_employer_costs',
				field: 'employer_costs',
				currentValue: values.employer_costs,
				suggestedValue: suggested,
				message: '', // Will be filled by translateCoverageIssue()
				severity: 'warning'
			});
		} else {
			// If ≤ 30% but not standard, it's just non-standard
			issues.push({
				type: 'non_standard_employer_costs',
				field: 'employer_costs',
				currentValue: values.employer_costs,
				suggestedValue: suggested,
				message: '', // Will be filled by translateCoverageIssue()
				severity: 'info'
			});
		}
	}

	return issues;
}

/**
 * Helper to translate coverage issue messages
 * Should be called from a React component with access to i18n context
 */
export function translateCoverageIssue(
	issue: CoverageIssue,
	t: (key: string, params?: Record<string, unknown>) => string
): string {
	switch (issue.type) {
		case 'exotic_waiting_period':
			return `${t('quotes.settings.coverage.exoticWarning.waitingPeriodUnusual', { days: issue.currentValue })} ${t('common.suggested')}: ${issue.suggestedValue} ${t('quotes.settings.coverage.waitingPeriod.unit')}.`;
		case 'zero_first_year_coverage':
			return `${t('quotes.settings.coverage.exoticWarning.firstYearLow', { percentage: issue.currentValue })} ${t('common.suggested')}: 70%.`;
		case 'unusual_coverage_progression':
			return t('quotes.settings.coverage.exoticWarning.improvedCoverage');
		case 'high_employer_costs':
			return `${t('quotes.settings.coverage.exoticWarning.employerCostsHigh', { percentage: issue.currentValue })} ${t('common.suggested')}: ${issue.suggestedValue}%.`;
		case 'non_standard_employer_costs':
			return `${t('quotes.settings.coverage.exoticWarning.employerCostsNonStandard', { percentage: issue.currentValue })} ${t('common.suggested')}: ${issue.suggestedValue}%.`;
		default:
			return issue.message || '';
	}
}

/**
 * Checks if coverage values have any exotic characteristics
 */
export function hasExoticCoverage(values: CoverageValues): boolean {
	return detectCoverageIssues(values).length > 0;
}

/**
 * Compares two coverage configurations and returns true if they differ
 */
export function coverageValuesDiffer(current: CoverageValues, original: CoverageValues): boolean {
	return (
		current.waiting_period !== original.waiting_period ||
		current.employer_costs !== original.employer_costs ||
		current.coverage_percentage_first_year_first_half !==
			original.coverage_percentage_first_year_first_half ||
		current.coverage_percentage_first_year_second_half !==
			original.coverage_percentage_first_year_second_half ||
		current.coverage_percentage_second_year_first_half !==
			original.coverage_percentage_second_year_first_half ||
		current.coverage_percentage_second_year_second_half !==
			original.coverage_percentage_second_year_second_half
	);
}

/**
 * Generates a human-readable description of coverage differences
 */
export function describeCoverageDifferences(
	current: CoverageValues,
	original: CoverageValues
): string[] {
	const differences: string[] = [];

	if (current.waiting_period !== original.waiting_period) {
		differences.push(`Wachtdagen: ${original.waiting_period} → ${current.waiting_period}`);
	}

	if (current.employer_costs !== original.employer_costs) {
		differences.push(`Werkgeverslasten: ${original.employer_costs}% → ${current.employer_costs}%`);
	}

	if (
		current.coverage_percentage_first_year_first_half !==
			original.coverage_percentage_first_year_first_half ||
		current.coverage_percentage_first_year_second_half !==
			original.coverage_percentage_first_year_second_half
	) {
		differences.push(
			`Dekking 1e jaar: ${original.coverage_percentage_first_year_first_half}%/${original.coverage_percentage_first_year_second_half}% → ${current.coverage_percentage_first_year_first_half}%/${current.coverage_percentage_first_year_second_half}%`
		);
	}

	if (
		current.coverage_percentage_second_year_first_half !==
			original.coverage_percentage_second_year_first_half ||
		current.coverage_percentage_second_year_second_half !==
			original.coverage_percentage_second_year_second_half
	) {
		differences.push(
			`Dekking 2e jaar: ${original.coverage_percentage_second_year_first_half}%/${original.coverage_percentage_second_year_second_half}% → ${current.coverage_percentage_second_year_first_half}%/${current.coverage_percentage_second_year_second_half}%`
		);
	}

	return differences;
}

/**
 * Interface for coverage data used in formatting functions
 */
export interface CoverageData {
	waiting_period?: number | string;
	coverage_percentage_first_year_first_half?: number;
	coverage_percentage_first_year_second_half?: number;
	coverage_percentage_second_year_first_half?: number | null;
	coverage_percentage_second_year_second_half?: number | null;
}

/**
 * Type for i18next translation function
 */
export type TranslationFunction = (key: string) => string;

/**
 * Formats first year coverage display based on waiting period and coverage percentages
 * @param coverageData - Coverage data including waiting period and first year percentages
 * @param t - Translation function from i18next
 * @param emptyValue - Value to display when data is missing (default: ' - ')
 * @returns Formatted coverage string
 *
 * @example
 * // 30 waiting days, different percentages
 * formatFirstYearCoverage({ waiting_period: 30, coverage_percentage_first_year_first_half: 100, coverage_percentage_first_year_second_half: 90 }, t)
 * // Returns: "100% / 90%"
 *
 * @example
 * // 130 waiting days, show only second half
 * formatFirstYearCoverage({ waiting_period: 130, coverage_percentage_first_year_first_half: 0, coverage_percentage_first_year_second_half: 90 }, t)
 * // Returns: "90%"
 *
 * @example
 * // 260 waiting days, not applicable
 * formatFirstYearCoverage({ waiting_period: 260, coverage_percentage_first_year_first_half: 0, coverage_percentage_first_year_second_half: 0 }, t)
 * // Returns: "n.v.t." or "N/A" based on locale
 */
export function formatFirstYearCoverage(
	coverageData: CoverageData,
	t: TranslationFunction,
	emptyValue: string | null | undefined = ' - '
): string | null {
	const waitingPeriod = Number(coverageData?.waiting_period) || 0;
	const firstHalf = coverageData?.coverage_percentage_first_year_first_half;
	const secondHalf = coverageData?.coverage_percentage_first_year_second_half;

	// 260 waiting days: show n.v.t./N/A
	if (waitingPeriod >= 260) {
		return t('common.notApplicable');
	}

	// 130 waiting days: only show second half
	if (waitingPeriod >= 130) {
		return secondHalf !== undefined ? `${secondHalf}%` : (emptyValue ?? ' - ');
	}

	// Both halves are the same: show one percentage
	if (firstHalf === secondHalf) {
		return firstHalf !== undefined ? `${firstHalf}%` : (emptyValue ?? ' - ');
	}

	// Different percentages: show both with slash
	if (firstHalf !== undefined && secondHalf !== undefined) {
		return `${firstHalf}% / ${secondHalf}%`;
	}

	return emptyValue ?? ' - ';
}

/**
 * Formats second year coverage display based on coverage percentages
 * @param coverageData - Coverage data including second year percentages
 * @param emptyValue - Value to display when data is missing (default: ' - ')
 * @returns Formatted coverage string
 *
 * @example
 * // Different percentages
 * formatSecondYearCoverage({ coverage_percentage_second_year_first_half: 80, coverage_percentage_second_year_second_half: 70 })
 * // Returns: "80% / 70%"
 *
 * @example
 * // Same percentages
 * formatSecondYearCoverage({ coverage_percentage_second_year_first_half: 80, coverage_percentage_second_year_second_half: 80 })
 * // Returns: "80%"
 */
export function formatSecondYearCoverage(
	coverageData: CoverageData,
	emptyValue: string | null | undefined = ' - '
): string | null {
	const firstHalf = coverageData?.coverage_percentage_second_year_first_half;
	const secondHalf = coverageData?.coverage_percentage_second_year_second_half;

	// Both halves are the same or either is null: show one percentage
	if (
		firstHalf === secondHalf ||
		firstHalf === null ||
		secondHalf === null ||
		firstHalf === undefined ||
		secondHalf === undefined
	) {
		return firstHalf !== undefined && firstHalf !== null ? `${firstHalf}%` : (emptyValue ?? ' - ');
	}

	// Different percentages: show both with slash
	return `${firstHalf}% / ${secondHalf}%`;
}
